import React from "react";
import { motion } from "framer-motion";
import { ShieldCheck } from "lucide-react";

const STATS = [
  { stat: "250+",  label: "Businesses Empowered" },
  { stat: "35%",   label: "Avg. Improvement in Decision Speed" },
  { stat: "99.9%", label: "Reliability You Can Count On" },
  { stat: "20+",   label: "Industries Transformed" },
];

// Persona orb accents — used ONLY as ambient, heavily-blurred light on
// the dark left panel. Never as primary button/text color (Core Blue
// owns that on the right side), per the C-ORE brand package.
const ORBS = [
  { color: "#2B70C2", top: "-8%",  left: "-6%",  size: 380 },
  { color: "#7E58B7", top: "58%",  left: "78%",  size: 340 },
  { color: "#B44554", top: "82%",  left: "-4%",  size: 300 },
  { color: "#008698", top: "8%",   left: "72%",  size: 260 },
];

const NOISE_BG =
  "data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='120' height='120'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2' stitchTiles='stitch'/></filter><rect width='100%25' height='100%25' filter='url(%23n)'/></svg>";

/**
 * Shared dark brand pane for the auth screens (Login / Signup) — keeps
 * the "consistent first impression" the two pages are meant to share.
 * Purely presentational; hidden on mobile (<md).
 */
export default function AuthBrandPanel({ testidPrefix = "auth" }) {
  return (
    <aside className="relative hidden flex-col justify-between overflow-hidden bg-[#060A14] p-12 md:flex md:w-[46%] lg:w-[50%] lg:p-16 xl:p-20">
      <img
        src="https://images.unsplash.com/photo-1770530333405-6e1c3876a903?crop=entropy&cs=srgb&fm=jpg&q=85"
        alt=""
        aria-hidden="true"
        draggable="false"
        className="absolute inset-0 h-full w-full object-cover opacity-[0.15] mix-blend-luminosity"
      />
      {ORBS.map((o, i) => (
        <div
          key={i}
          aria-hidden="true"
          className="absolute rounded-full"
          style={{
            top: o.top, left: o.left, width: o.size, height: o.size,
            background: o.color, filter: "blur(120px)", opacity: 0.35,
          }}
        />
      ))}
      <div
        aria-hidden="true"
        className="absolute inset-0 opacity-[0.05]"
        style={{ backgroundImage: `url("${NOISE_BG}")` }}
      />

      <div className="relative z-10">
        <motion.img
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.5 }}
          src="/c-ore-logo.png" alt="C-ORE" draggable="false"
          style={{ height: 44, width: 160, objectFit: "contain" }}
        />

        <motion.h1
          initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
          className="mt-12 font-display text-4xl font-semibold leading-[1.1] tracking-tight text-white lg:text-5xl"
        >
          Business Operating System<br />
          <span className="text-cyan-300">with C-Suite Excellence.</span>
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="mt-4 max-w-lg font-sans text-[15px] leading-relaxed text-ink-300"
        >
          Activate your C-SUITE in 60 minutes. Let CEO, CFO, CMO &amp;
          CTO drive your business.
        </motion.p>

        <motion.div
          initial="hidden" animate="show"
          variants={{ show: { transition: { staggerChildren: 0.08, delayChildren: 0.32 } } }}
          className="mt-12 grid max-w-2xl grid-cols-2 gap-4 lg:gap-5"
        >
          {STATS.map((s) => (
            <motion.div
              key={s.stat}
              variants={{ hidden: { opacity: 0, y: 10 }, show: { opacity: 1, y: 0 } }}
              data-testid={`${testidPrefix}-stat-${s.stat}`}
              className="rounded-card border border-white/[0.08] bg-white/[0.03] p-5 shadow-[0_8px_32px_rgba(0,0,0,0.25)] backdrop-blur-xl transition-colors duration-300 hover:border-white/[0.14] hover:bg-white/[0.05]"
            >
              <div className="font-mono text-3xl font-light tracking-tight text-white lg:text-[34px]">
                {s.stat}
              </div>
              <div className="mt-1.5 font-sans text-[11px] uppercase tracking-widest text-ink-400">
                {s.label}
              </div>
            </motion.div>
          ))}
        </motion.div>
      </div>

      <motion.div
        initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.7 }}
        className="relative z-10 mt-14 inline-flex items-center gap-3 rounded-2xl border border-white/[0.08] bg-white/[0.03] px-6 py-4 backdrop-blur-md"
      >
        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-white/[0.06]">
          <ShieldCheck size={15} className="text-cyan-300" />
        </span>
        <div>
          <div className="font-sans text-[13.5px] font-semibold text-white">
            Trusted by forward-thinking leadership teams.
          </div>
          <div className="mt-0.5 font-sans text-[12px] text-ink-400">
            Secure. Unified. Integrated. Built for impact.
          </div>
        </div>
      </motion.div>
    </aside>
  );
}
